import { createEntityAdapter, createSelector, createSlice, current, EntityState, PayloadAction } from "@reduxjs/toolkit";
import { has, isObject } from "lodash-es";

import { Model, CustomizationBase, OutputMetadataItem, AeTitleRule, DicomRule, DicomAttributeRule, ModelVisibility, SelectedDoseUnit, CustomizationObjectType, METADATA_FILENAME_ATTRIBUTE, Modality, ModalityDirection } from "../global-types/customization-types";
import { DoseCustomizationEntities, DoseCustomizationOutput, DoseCustomizationOutputEntities, DoseRoi, DoseScalingMethod, DoseTarget, TargetMethod } from "./dose-types";
import { BackendValidationErrorViewModel, FormValidationError, FormValidationErrorSeverity } from "../global-types/store-errors";
import { naturalSort } from "../../util/sort";
import { DoseSliceWrapper } from "../global-types/modelTypeReducerHelper/doseSliceWrapper";
import { defaultCreateNewDoseTarget } from "./dose-helpers";
import { MVisionAppClient } from "../configurationTarget/mvision-client-list";
import { BackendValidationError, ConcurrentSaveError } from "../../util/errors";
import { DuplicatedIdMap, setObjectAndAncestorsAsModified, updateModelTimestampsAfterSave } from "../global-types/reducer-helpers";
import { LengthValue } from "../global-types/units";
import { composeDoseOutputValidationErrors, composeTriggerValidationErrors } from "../global-types/validation/selectors";




// entity adapters (see https://redux-toolkit.js.org/api/createEntityAdapter) for dose customization types
export const modelAdapter = createEntityAdapter<Model>();
export const customizationBaseAdapter = createEntityAdapter<CustomizationBase>();
export const doseOutputAdapter = createEntityAdapter<DoseCustomizationOutput>();
export const outputMetadataAdapter = createEntityAdapter<OutputMetadataItem>();
export const doseRoiAdapter = createEntityAdapter<DoseRoi>();
export const doseTargetAdapter = createEntityAdapter<DoseTarget>();
export const aeTitleRuleAdapter = createEntityAdapter<AeTitleRule>();
export const dicomRuleAdapter = createEntityAdapter<DicomRule>();
export const dicomAttributeRuleAdapter = createEntityAdapter<DicomAttributeRule>();

// entity adapter selectors -- these should not be exported/used directly but rather through the slice selectors
export const modelAdapterSelectors = modelAdapter.getSelectors((state: DoseSliceState) => state.models);
export const customizationBaseAdapterSelectors = customizationBaseAdapter.getSelectors((state: DoseSliceState) => state.customizationBases);
export const doseOutputAdapterSelectors = doseOutputAdapter.getSelectors((state: DoseSliceState) => state.doseOutputs);
export const outputMetadataAdapterSelectors = outputMetadataAdapter.getSelectors((state: DoseSliceState) => state.outputMetadata);
export const doseRoiAdapterSelectors = doseRoiAdapter.getSelectors((state: DoseSliceState) => state.doseRois);
export const doseTargetAdapterSelectors = doseTargetAdapter.getSelectors((state: DoseSliceState) => state.doseTargets);
export const aeTitleRuleAdapterSelectors = aeTitleRuleAdapter.getSelectors((state: DoseSliceState) => state.aeTitleRules);
export const dicomRuleAdapterSelectors = dicomRuleAdapter.getSelectors((state: DoseSliceState) => state.dicomRules);
export const dicomAttributeRuleAdapterSelectors = dicomAttributeRuleAdapter.getSelectors((state: DoseSliceState) => state.dicomAttributeRules);

/** An entity adapter for CRUD operations for backend-based customization validation errors */
export const backendValidationErrorAdapter = createEntityAdapter<BackendValidationErrorViewModel>();
// selectors for 'original' data
export const originalOutputMetadataAdapterSelectors = outputMetadataAdapter.getSelectors((state: DoseSliceState) => state.originalOutputMetadata);
export const originalDoseRoiAdapterSelectors = doseRoiAdapter.getSelectors((state: DoseSliceState) => state.originalDoseRois);
export const originalDoseTargetAdapterSelectors = doseTargetAdapter.getSelectors((state: DoseSliceState) => state.originalDoseTargets);

// validation error selectors
export const customizationValidationErrorAdapterSelectors = backendValidationErrorAdapter.getSelectors((state: DoseSliceState) => state.backendValidationErrors);

export const isDoseSliceState = (obj: unknown): obj is DoseSliceState => {
    return has(obj, 'models') &&
        has(obj, 'doseOutputs') &&
        has(obj, 'doseRois') &&
        has(obj, 'doseTargets') &&
        has(obj, 'doseRois') &&
        has(obj.doseRois, 'entities') &&
        isObject(obj.doseRois.entities);
}

export type DoseSliceState = {
    /** In-memory CRUD-based normalized data structure for customization objects modelling dose models. */
    models: EntityState<Model, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling customization base objects. */
    customizationBases: EntityState<CustomizationBase, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling customization outputs. */
    doseOutputs: EntityState<DoseCustomizationOutput, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling customization metadata. */
    outputMetadata: EntityState<OutputMetadataItem, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling dose ROI customizations. */
    doseRois: EntityState<DoseRoi, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling dose targets. */
    doseTargets: EntityState<DoseTarget, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling AE Title-based model selection rules. */
    aeTitleRules: EntityState<AeTitleRule, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling DICOM-based model selection rules. */
    dicomRules: EntityState<DicomRule, string>,
    /** In-memory CRUD-based normalized data structure for customization objects modelling DICOM attribute rules within DICOM-based model selection rules. */
    dicomAttributeRules: EntityState<DicomAttributeRule, string>,

    // these are for storing data directly from the backend, so any local modifications can be easily reverted back to them

    /** Original, unmodified directly-from-backend versions of dose model customization data objects. These are stored 
     * mainly so reverting changes back to original data is easy.
     */
    originalModels: EntityState<Model, string>;
    /** Original, unmodified directly-from-backend versions of dose customization base data objects. These are stored 
     * mainly so reverting changes back to original data is easy.
     */
    originalCustomizationBases: EntityState<CustomizationBase, string>;
    /** Original, unmodified directly-from-backend versions of dose customization output data objects. These are stored 
     * mainly so reverting changes back to original data is easy.
     */
    originalDoseOutputs: EntityState<DoseCustomizationOutput, string>;
    /** Original, unmodified directly-from-backend versions of dose customization metadata objects. These are stored 
     * mainly so reverting changes back to original data is easy.
     */
    originalOutputMetadata: EntityState<OutputMetadataItem, string>;
    /** Original, unmodified directly-from-backend versions of dose ROI customization objects. These are stored 
     * mainly so reverting changes back to original data is easy.
     */
    originalDoseRois: EntityState<DoseRoi, string>;
    /** Original, unmodified directly-from-backend versions of dose target customization objects. These are stored 
     * mainly so reverting changes back to original data is easy.
     */
    originalDoseTargets: EntityState<DoseTarget, string>;
    /** Original, unmodified directly-from-backend versions of dose AE Title rule data objects. These are stored 
     * mainly so reverting changes back to original data is easy.
     */
    originalAeTitleRules: EntityState<AeTitleRule, string>;
    /** Original, unmodified directly-from-backend versions of dose DICOM rule data objects. These are stored 
     * mainly so reverting changes back to original data is easy.
     */
    originalDicomRules: EntityState<DicomRule, string>;
    /** Original, unmodified directly-from-backend versions of dose DICOM attribute rule data objects. These are stored 
     * mainly so reverting changes back to original data is easy.
     */
    originalDicomAttributeRules: EntityState<DicomAttributeRule, string>;


    /** Dose customization validation errors coming from backend. */
    backendValidationErrors: EntityState<BackendValidationErrorViewModel, string>,

    /** An error that occurred when fetching dose customizations, or null. */
    customizationFetchError: string | null,

    /** Saving of model customizations to backend is in progress if true, false otherwise. */
    isModelCustomizationSavingInProgress: boolean,

    /** True if resetting all dose customizations to factory defaults is currently in progress, false otherwise. */
    isAllModelCustomizationsResetInProgress: boolean,

    /** An error that occurred when resetting all model customizations to factory defaults, or null if no error occurred. */
    allModelCustomizationsResetError: string | null,

    /** True if resetting a single dose customization output to factory defaults is currently in progress, false otherwise. */
    isOutputResetInProgress: boolean,

    /** Model customization save error, if any. */
    modelCustomizationSaveError: string | null,

    /** A concurrent save error, if any. Set when the backend detects another user has saved changes. */
    concurrentSaveError: ConcurrentSaveError | null,

    /** An error regarding model customization data that's not directly related to saving */
    modelCustomizationDataError: string | null,

    /** An error message regarding exporting or importing entire customization config to/from JSON */
    customizationImportExportError: string | null,

    /** True if a PDF export is currently in progress. */
    pdfExportInProgress: boolean,

    /** Current user selection of tags to filter models by. No filtering is applied if empty. */
    activeTagFilters: string[],

    /** Current user selection of input modalities to filter models by. No filtering is applied if empty. */
    activeInputModalityFilters: Modality[],

    /** Current user selection of output modalities to filter models by. No filtering is applied if empty. */
    activeOutputModalityFilters: Modality[],

    /** Current user selection of version tags to filter models by. No filtering is applied if empty. */
    activeVersionTags: string[],

    /** Current user selection of free-text filter value. No filtering is applied if empty. */
    activeFreeTextFilter: string,

    /** True if deprecated models are shown, false otherwise. */
    areDeprecatedModelsShown: boolean,

    /** True if models not within current license are shown, false otherwise. */
    areNonLicensedModelsShown: boolean,

    /** True if models hidden by current user are shown, false otherwise. */
    areUserHiddenModelsShown: boolean,
};





export const initialState: DoseSliceState = {
    models: modelAdapter.getInitialState(),
    customizationBases: customizationBaseAdapter.getInitialState(),
    doseOutputs: doseOutputAdapter.getInitialState(),
    outputMetadata: outputMetadataAdapter.getInitialState(),
    doseRois: doseRoiAdapter.getInitialState(),
    doseTargets: doseTargetAdapter.getInitialState(),
    aeTitleRules: aeTitleRuleAdapter.getInitialState(),
    dicomRules: dicomRuleAdapter.getInitialState(),
    dicomAttributeRules: dicomAttributeRuleAdapter.getInitialState(),

    originalModels: modelAdapter.getInitialState(),
    originalCustomizationBases: customizationBaseAdapter.getInitialState(),
    originalDoseOutputs: doseOutputAdapter.getInitialState(),
    originalOutputMetadata: outputMetadataAdapter.getInitialState(),
    originalDoseRois: doseRoiAdapter.getInitialState(),
    originalDoseTargets: doseTargetAdapter.getInitialState(),
    originalAeTitleRules: aeTitleRuleAdapter.getInitialState(),
    originalDicomRules: dicomRuleAdapter.getInitialState(),
    originalDicomAttributeRules: dicomAttributeRuleAdapter.getInitialState(),

    backendValidationErrors: backendValidationErrorAdapter.getInitialState(),

    customizationFetchError: null,
    isModelCustomizationSavingInProgress: false,
    isAllModelCustomizationsResetInProgress: false,
    allModelCustomizationsResetError: null,
    isOutputResetInProgress: false,
    modelCustomizationSaveError: null,
    concurrentSaveError: null,
    modelCustomizationDataError: null,
    customizationImportExportError: null,
    pdfExportInProgress: false,
    activeTagFilters: [],
    activeInputModalityFilters: [],
    activeOutputModalityFilters: [],
    activeVersionTags: [],
    activeFreeTextFilter: '',
    areDeprecatedModelsShown: false,
    areNonLicensedModelsShown: false,
    areUserHiddenModelsShown: false,
};



const sliceWrapper = new DoseSliceWrapper();

const getOutputOrThrow = (state: DoseSliceState, id: string) => {
    const output = state.doseOutputs.entities[id];
    if (output === undefined) { throw new Error(`Could not find dose output with id ${id}`); }
    return output;
};

/** Redux store slice for interacting with dose+ configuration and customization. */
const doseSlice = createSlice({
    name: 'dose',
    initialState,
    reducers: {
        /**
         * Sets current dose model customization entities to provided values. This is used when initializing, swapping, or
         * resetting entire model customizations loaded into memory.
         * @param action.customizations All dose-related model customization entities to load into memory. If set to undefined then
         * in-model entities are reset to default (not original!) empty values.
         * @param action.errorMessage Optional error message.
         * @param action.retainOriginalData If true, the original entities are not reset to the new values, but rather kept as they are.  This is
         * relevant when e.g. setting customizations during JSON import where we want to keep the original values intact so that we can
         * still perform a full undo to pre-import values. Note: ignored if customizations is set to null, in which case the original data is 
         * always reset to default (not original!) values.
         */
        modelCustomizationsSet(state, action: PayloadAction<{ customizations: DoseCustomizationEntities | null, errorMessage?: string, retainOriginalData?: boolean }>) {
            const { customizations, errorMessage, retainOriginalData } = action.payload;

            if (customizations !== null) {
                // set everything according to input
                modelAdapter.setAll(state.models, customizations.models);
                customizationBaseAdapter.setAll(state.customizationBases, customizations.customizationBases);
                doseOutputAdapter.setAll(state.doseOutputs, customizations.doseOutputs);
                outputMetadataAdapter.setAll(state.outputMetadata, customizations.outputMetadata);
                doseRoiAdapter.setAll(state.doseRois, customizations.doseRois);
                doseTargetAdapter.setAll(state.doseTargets, customizations.doseTargets);
                aeTitleRuleAdapter.setAll(state.aeTitleRules, customizations.aeTitleRules);
                dicomRuleAdapter.setAll(state.dicomRules, customizations.dicomRules);
                dicomAttributeRuleAdapter.setAll(state.dicomAttributeRules, customizations.dicomAttributeRules);

                // set original collections, also, so we can revert changes to the actual collection with ease
                if (!retainOriginalData) {
                    modelAdapter.setAll(state.originalModels, customizations.models);
                    customizationBaseAdapter.setAll(state.originalCustomizationBases, customizations.customizationBases);
                    doseOutputAdapter.setAll(state.originalDoseOutputs, customizations.doseOutputs);
                    outputMetadataAdapter.setAll(state.originalOutputMetadata, customizations.outputMetadata);
                    doseRoiAdapter.setAll(state.originalDoseRois, customizations.doseRois);
                    doseTargetAdapter.setAll(state.originalDoseTargets, customizations.doseTargets);
                    aeTitleRuleAdapter.setAll(state.originalAeTitleRules, customizations.aeTitleRules);
                    dicomRuleAdapter.setAll(state.originalDicomRules, customizations.dicomRules);
                    dicomAttributeRuleAdapter.setAll(state.originalDicomAttributeRules, customizations.dicomAttributeRules);
                }
            } else {
                // set everything to default values
                modelAdapter.setAll(state.models, modelAdapter.getInitialState().entities as Record<string, Model>);
                customizationBaseAdapter.setAll(state.customizationBases, customizationBaseAdapter.getInitialState().entities as Record<string, CustomizationBase>);
                doseOutputAdapter.setAll(state.doseOutputs, doseOutputAdapter.getInitialState().entities as Record<string, DoseCustomizationOutput>);
                outputMetadataAdapter.setAll(state.outputMetadata, outputMetadataAdapter.getInitialState().entities as Record<string, OutputMetadataItem>);
                doseRoiAdapter.setAll(state.doseRois, doseRoiAdapter.getInitialState().entities as Record<string, DoseRoi>);
                doseTargetAdapter.setAll(state.doseTargets, doseTargetAdapter.getInitialState().entities as Record<string, DoseTarget>);
                aeTitleRuleAdapter.setAll(state.aeTitleRules, aeTitleRuleAdapter.getInitialState().entities as Record<string, AeTitleRule>);
                dicomRuleAdapter.setAll(state.dicomRules, dicomRuleAdapter.getInitialState().entities as Record<string, DicomRule>);
                dicomAttributeRuleAdapter.setAll(state.dicomAttributeRules, dicomAttributeRuleAdapter.getInitialState().entities as Record<string, DicomAttributeRule>);

                // set original collections, also, so we can revert changes to the actual collection with ease
                modelAdapter.setAll(state.originalModels, modelAdapter.getInitialState().entities as Record<string, Model>);
                customizationBaseAdapter.setAll(state.originalCustomizationBases, customizationBaseAdapter.getInitialState().entities as Record<string, CustomizationBase>);
                doseOutputAdapter.setAll(state.originalDoseOutputs, doseOutputAdapter.getInitialState().entities as Record<string, DoseCustomizationOutput>);
                outputMetadataAdapter.setAll(state.originalOutputMetadata, outputMetadataAdapter.getInitialState().entities as Record<string, OutputMetadataItem>);
                doseRoiAdapter.setAll(state.originalDoseRois, doseRoiAdapter.getInitialState().entities as Record<string, DoseRoi>);
                doseTargetAdapter.setAll(state.originalDoseTargets, doseTargetAdapter.getInitialState().entities as Record<string, DoseTarget>);
                aeTitleRuleAdapter.setAll(state.originalAeTitleRules, aeTitleRuleAdapter.getInitialState().entities as Record<string, AeTitleRule>);
                dicomRuleAdapter.setAll(state.originalDicomRules, dicomRuleAdapter.getInitialState().entities as Record<string, DicomRule>);
                dicomAttributeRuleAdapter.setAll(state.originalDicomAttributeRules, dicomAttributeRuleAdapter.getInitialState().entities as Record<string, DicomAttributeRule>);
            }

            state.customizationFetchError = errorMessage !== undefined ? errorMessage : null;
            if (errorMessage === undefined) {
                // clear any errors
                backendValidationErrorAdapter.removeAll(state.backendValidationErrors);
                sliceWrapper.clearSaveErrors(state);
            }
        },

        /**
         * Updates a dose model's visibility.
         * @param action.modelId Internal (entity adapter) ID of the dose model to update.
         * @param action.visibility The new visibility setting for the dose model.
         */
        modelVisibilityUpdated(state, action: PayloadAction<{ modelId: string, visibility: ModelVisibility }>) {
            const { modelId, visibility } = action.payload;
            sliceWrapper.updateModelVisibility(state, modelId, visibility);
        },

        /**
        * Updates the description field on a customization base object.
        * @param action.customizationBaseId Internal (entity adapter) ID of the customization base to update.
        * @param action.description The new description to set.
        */
        modelCustomizationDescriptionUpdated(state, action: PayloadAction<{ customizationBaseId: string, description: string }>) {
            const { customizationBaseId, description } = action.payload;
            sliceWrapper.updateModelCustomizationDescription(state, customizationBaseId, description);
        },

        /**
         * Updates an output metadata item tuple. If either attribute or value is left as undefined then that particular prop is not updated
         * and the existing prop is left unchanged.
         * @param action.metadataId Internal (entity adapter) ID of the output metadata item that will be updated.
         * @param action.attribute The new attribute name of the metadata item that will be used, or undefined if attribute should not be changed.
         * @param action.value The new value of the metadata item, or undefined if value should not be changed.
         * @param action.isUndoOperation Marks this update as part of an undo operation. Note that the correct reverted attribute and value props
         * must be supplied to this function manually, this function does not perform an undo by itself. Optional, defaults to false.
         */
        metadataItemUpdated(state, action: PayloadAction<{ metadataId: string, attribute: string | undefined, value: string | undefined, isUndoOperation?: boolean }>) {
            const { metadataId, attribute, value, isUndoOperation } = action.payload;
            sliceWrapper.updateMetadataItem(state, metadataId, attribute, value, isUndoOperation);
        },

        /**
         * Removes an output metadata item.
         * @param action.metadataId Internal (entity adapter) ID of the output metadata item that will be removed.
         */
        metadataItemRemoved(state, action: PayloadAction<string>) {
            const metadataId = action.payload;
            sliceWrapper.removeMetadataItem(state, metadataId);
        },

        /**
         * Removes a tag from a (newly created) customization metadata item that would mark it as being the target of being
         * automatically scrolled to in UI.
         * 
         * This tag is generally given to metadata items after their creation in the UI and should be removed once
         * the UI has scrolled to the correct position in the UI once.
         * 
         * @param action Internal (entity adapter) ID of the customization metadata item where this tag is to be removed.
         */
        scrollToViewFromModelCustomizationMetadataRemoved(state, action: PayloadAction<string>) {
            const metadataId = action.payload;
            sliceWrapper.removeScrollToViewFromMetadata(state, metadataId);
        },

        /**
        * Adds supplied AE Title rule into dose customization configuration.
        * @param action The AE Title rule to add.
        */
        aeTitleRuleAdded(state, action: PayloadAction<AeTitleRule>) {
            const aeTitleRule = action.payload;
            sliceWrapper.addAeTitleRule(state, aeTitleRule);
        },

        /**
         * Updates the 'action' field in an AE Title rule.
         * @param action.id Internal (entity adapter) ID of the AE Title rule to update.
         * @param action.action The new action value for the rule.
         */
        aeTitleRuleActionUpdated(state, action: PayloadAction<{ id: string, action: string }>) {
            const { id, action: newAction } = action.payload;
            sliceWrapper.updateAeTitleRuleAction(state, id, newAction);
        },

        /**
         * Removes an AE Title rule from dose customization configuration.
         * Throws if the AE Title rule to be removed is not found from configuration.
         * @param action Internal (entity adapter) ID of the AE Title rule to remove.
         */
        aeTitleRuleRemoved(state, action: PayloadAction<string>) {
            const id = action.payload;
            sliceWrapper.removeAeTitleRule(state, id);
        },

        /**
         * Adds supplied DICOM rule into dose customization configuration.
         * Note that DICOM attribute rules under a DICOM rule must be added separately.
         * @param action The DICOM rule to add.
         */
        dicomRuleAdded(state, action: PayloadAction<DicomRule>) {
            const dicomRule = action.payload;
            sliceWrapper.addDicomRule(state, dicomRule);
        },

        /**
         * Removes a DICOM rule from dose customization configuration. Also removes any DICOM attribute
         * rules under the DICOM rule.
         * Throws if the DICOM rule to be removed is not found from configuration.
         * @param action Internal (entity adapter) ID of the DICOM rule to remove.
         */
        dicomRuleRemoved(state, action: PayloadAction<string>) {
            const id = action.payload;
            sliceWrapper.removeDicomRule(state, id);
        },

        /**
        * Adds supplied DICOM attribute rule into dose customization configuration.
        * Note that the parent DICOM rule must already exist in configuration or this function will throw.
        * @param action The DICOM attribute rule to add.
        */
        dicomAttributeRuleAdded(state, action: PayloadAction<DicomAttributeRule>) {
            const dicomAttributeRule = action.payload;
            sliceWrapper.addDicomAttributeRule(state, dicomAttributeRule);
        },

        /**
         * Removes a DICOM attribute rule from dose customization configuration.
         * Throws if the DICOM attribute rule to be removed or its parent DICOM rule are not found from configuration.
         * @param action Internal (entity adapter) ID of the DICOM attribute rule to remove.
         */
        dicomAttributeRuleRemoved(state, action: PayloadAction<string>) {
            const id = action.payload;
            sliceWrapper.removeDicomAttributeRule(state, id);
        },

        /**
         * Updates a DICOM attribute rule.
         * @param action.id Internal (entity adapter) ID of the DICOM attribute rule to update.
         * @param action.dicomAttribute The attribute field of the rule is updated to this value.
         * @param action.dicomValue The DICOM value field of the rule is updated to this value.
         */
        dicomAttributeRuleUpdated(state, action: PayloadAction<{ id: string, dicomAttribute: string, dicomValue: string }>) {
            const { id, dicomAttribute, dicomValue } = action.payload;
            sliceWrapper.updateDicomAttributeRule(state, id, dicomAttribute, dicomValue);
        },

        /**
         * Updates a dose output's dose scaling method.
         * @param action.id ID of the dose output.
         * @param action.method The value to set the dose scaling's method field to.
         */
        doseScalingMethodSet(state, action: PayloadAction<{ id: string, method: DoseScalingMethod }>) {
            const { id, method } = action.payload;

            const output = state.doseOutputs.entities[id];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${id}`); }

            const doseScaling = output.doseScaling;
            doseScaling.method = method;
            doseOutputAdapter.updateOne(state.doseOutputs, { id: id, changes: { doseScaling } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Updates a dose output's dose scaling's volume field.
         * @param action.id ID of the dose output.
         * @param action.volume The value to set the volume field to.
         */
        doseScalingVolumeSet(state, action: PayloadAction<{ id: string, volume: number }>) {
            const { id, volume } = action.payload;

            const output = state.doseOutputs.entities[id];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${id}`); }

            const doseScaling = output.doseScaling;
            doseScaling.volume = volume;
            doseOutputAdapter.updateOne(state.doseOutputs, { id: id, changes: { doseScaling } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Updates a dose output's dose scaling's dose field.
         * @param action.id ID of the dose output.
         * @param action.dose The value to set the dose field to.
         */
        doseScalingDoseSet(state, action: PayloadAction<{ id: string, dose: number }>) {
            const { id, dose } = action.payload;

            const output = state.doseOutputs.entities[id];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${id}`); }

            const doseScaling = output.doseScaling;
            doseScaling.dose = dose;
            doseOutputAdapter.updateOne(state.doseOutputs, { id: id, changes: { doseScaling } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Updates a dose output's pixel spacing.
         * @param action.id ID of the dose output.
         * @param pixelSpacing The value to set pixel spacing to.
         */
        pixelSpacingSet(state, action: PayloadAction<{ id: string, pixelSpacing: LengthValue }>) {
            const { id, pixelSpacing } = action.payload;

            const output = state.doseOutputs.entities[id];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${id}`); }

            doseOutputAdapter.updateOne(state.doseOutputs, { id: id, changes: { pixelSpacing } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Sets a dose output's dose cropping to on or off.
         * @param action.id ID of the dose output.
         * @param action.isEnabled The value to set the isEnabled field to.
         */
        doseCroppingIsEnabledSet(state, action: PayloadAction<{ id: string, isEnabled: boolean }>) {
            const { id, isEnabled } = action.payload;

            const output = state.doseOutputs.entities[id];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${id}`); }

            const doseCropping = output.doseCropping;
            doseCropping.isEnabled = isEnabled;
            doseOutputAdapter.updateOne(state.doseOutputs, { id: id, changes: { doseCropping: doseCropping } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Sets a dose output's Z-margin cropping value.
         * @param action.id ID of the dose output.
         * @param action.value The value to set the z-margin cropping to.
         */
        doseCroppingValueSet(state, action: PayloadAction<{ id: string, zMargin: LengthValue }>) {
            const { id, zMargin } = action.payload;

            const output = state.doseOutputs.entities[id];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${id}`); }

            const doseCropping = output.doseCropping;
            doseCropping.zMargin = zMargin;
            doseOutputAdapter.updateOne(state.doseOutputs, { id: id, changes: { doseCropping: doseCropping } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Sets whether or not RT Plan is included with returned dose.
         * @param action.id ID of the dose output.
         * @param action.isIncluded The value to set the isRTPlanIncluded field to.
         */
        outputIsRTPlanIncludedSet(state, action: PayloadAction<{ id: string, isIncluded: boolean }>) {
            const { id, isIncluded } = action.payload;

            const output = state.doseOutputs.entities[id];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${id}`); }

            doseOutputAdapter.updateOne(state.doseOutputs, { id: id, changes: { isRTPlanIncluded: isIncluded } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Sets whether or not RT Plan beam sequence is included with returned dose.
         * @param action.id ID of the dose output.
         * @param action.isIncluded The value to set the isBeamIncluded field to.
         */
        outputIsBeamIncludedSet(state, action: PayloadAction<{ id: string, isIncluded: boolean }>) {
            const { id, isIncluded } = action.payload;

            const output = state.doseOutputs.entities[id];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${id}`); }

            doseOutputAdapter.updateOne(state.doseOutputs, { id: id, changes: { isBeamIncluded: isIncluded } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Sets dose output's machine type.
         * @param action.id ID of the dose output.
         * @param action.machineType The value to set the machineType field to.
         */
        outputMachineTypeSet(state, action: PayloadAction<{ id: string, machineType: string }>) {
            const { id, machineType } = action.payload;

            const output = state.doseOutputs.entities[id];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${id}`); }

            doseOutputAdapter.updateOne(state.doseOutputs, { id: id, changes: { machineType } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Sets dose output's machine name.
         * @param action.id ID of the dose output.
         * @param action.machineType The value to set the machineName field to.
         */
        outputMachineNameSet(state, action: PayloadAction<{ id: string, machineName: string }>) {
            const { id, machineName } = action.payload;

            const output = state.doseOutputs.entities[id];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${id}`); }

            doseOutputAdapter.updateOne(state.doseOutputs, { id: id, changes: { machineName } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        // *** Optimization ROI creation reducers ***

        optimizationCreateRoisSet(state, action: PayloadAction<{ id: string, isRoiCreationEnabled: boolean }>) {
            const { id, isRoiCreationEnabled } = action.payload;
            const output = getOutputOrThrow(state, id);
            output.optimization.isRoiCreationEnabled = isRoiCreationEnabled;
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        optimizationPrefixSet(state, action: PayloadAction<{ id: string, prefix: string }>) {
            const { id, prefix } = action.payload;
            const output = getOutputOrThrow(state, id);
            output.optimization.prefix = prefix;
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        optimizationTargetUnionRingsEnabledSet(state, action: PayloadAction<{ id: string, isEnabled: boolean }>) {
            const { id, isEnabled } = action.payload;
            const output = getOutputOrThrow(state, id);
            output.optimization.targetUnionRings.isEnabled = isEnabled;
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        optimizationTargetUnionRingsWidthSet(state, action: PayloadAction<{ id: string, width: number }>) {
            const { id, width } = action.payload;
            const output = getOutputOrThrow(state, id);
            output.optimization.targetUnionRings.width = width;
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        optimizationTargetUnionRingsDistanceSet(state, action: PayloadAction<{ id: string, index: number, value: number }>) {
            const { id, index, value } = action.payload;
            const output = getOutputOrThrow(state, id);
            const distances = output.optimization.targetUnionRings.distances;
            if (index >= 0 && index < distances.length) {
                distances[index] = value;
            }
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        optimizationSkinEnabledSet(state, action: PayloadAction<{ id: string, isEnabled: boolean }>) {
            const { id, isEnabled } = action.payload;
            const output = getOutputOrThrow(state, id);
            output.optimization.skin.isEnabled = isEnabled;
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        optimizationSkinWidthSet(state, action: PayloadAction<{ id: string, width: number }>) {
            const { id, width } = action.payload;
            const output = getOutputOrThrow(state, id);
            output.optimization.skin.width = width;
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        optimizationTargetSubtractionEnabledSet(state, action: PayloadAction<{ id: string, isEnabled: boolean }>) {
            const { id, isEnabled } = action.payload;
            const output = getOutputOrThrow(state, id);
            output.optimization.targetSubtraction.isEnabled = isEnabled;
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        optimizationTargetSubtractionMarginSet(state, action: PayloadAction<{ id: string, margin: number }>) {
            const { id, margin } = action.payload;
            const output = getOutputOrThrow(state, id);
            output.optimization.targetSubtraction.margin = margin;
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        optimizationPlanningRiskVolumesEnabledSet(state, action: PayloadAction<{ id: string, isEnabled: boolean }>) {
            const { id, isEnabled } = action.payload;
            const output = getOutputOrThrow(state, id);
            output.optimization.planningRiskVolumes.isEnabled = isEnabled;
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        optimizationPlanningRiskVolumesMarginSet(state, action: PayloadAction<{ id: string, margin: number }>) {
            const { id, margin } = action.payload;
            const output = getOutputOrThrow(state, id);
            output.optimization.planningRiskVolumes.margin = margin;
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Sets a dose ROI's name.
         * @param action.id ID of the dose ROI.
         * @param action.name The value to set the name to.
         */
        doseRoiNameSet(state, action: PayloadAction<{ id: string, name: string }>) {
            const { id, name } = action.payload;

            const roi = state.doseRois.entities[id];
            if (roi === undefined) { throw new Error(`Could not find dose ROI with id ${id}`); }

            doseRoiAdapter.updateOne(state.doseRois, { id: id, changes: { name } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.Roi);
        },

        /**
         * Sets a dose ROI's regular expression (match pattern).
         * @param action.id ID of the dose ROI.
         * @param action.regExp The value to set the regExp field to.
         */
        doseRoiRegExpSet(state, action: PayloadAction<{ id: string, regExp: string }>) {
            const { id, regExp } = action.payload;

            const roi = state.doseRois.entities[id];
            if (roi === undefined) { throw new Error(`Could not find dose ROI with id ${id}`); }

            doseRoiAdapter.updateOne(state.doseRois, { id: id, changes: { regExp } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.Roi);
        },

        /**
         * Reverts a dose ROI to its original values (if available).
         * @param action ID of the dose ROI.
         */
        doseRoiChangesReverted(state, action: PayloadAction<string>) {
            const roiId = action.payload;

            const originalRoi = state.originalDoseRois.entities[roiId];
            if (originalRoi) {
                doseRoiAdapter.upsertOne(state.doseRois, { ...originalRoi, });

                // remove any validation errors
                backendValidationErrorAdapter.removeOne(state.backendValidationErrors, roiId);
            }
        },

        /**
         * Sets target method for dose output.
         * @param action.outputId ID of the dose output.
         * @param action.targetMethod The value to set the method field to.
         */
        doseTargetMethodSet(state, action: PayloadAction<{ outputId: string, targetMethod: TargetMethod }>) {
            const { outputId, targetMethod } = action.payload;

            const output = state.doseOutputs.entities[outputId];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${outputId}`); }

            // Automatic swapping between target regexes no longer makes sense now that the default regex for
            // fixed method is an empty string. Code left here for posterity in case this changes in near future.
            //
            // const isMethodChanged = output.targetMethod !== targetMethod &&
            //     output.targetMethod !== TargetMethod.NotSet &&
            //     targetMethod !== TargetMethod.NotSet;

            // if (isMethodChanged) {
            //     // change all default regexes to the other default regex
            //     const previousDefaultRegex = getDefaultRegexForTargetMethod(output.targetMethod);
            //     const newDefaultRegex = getDefaultRegexForTargetMethod(targetMethod);

            //     for (const targetId of output.targets) {
            //         const target = state.doseTargets.entities[targetId];
            //         if (target === undefined) { throw new Error(`Could not find dose target with id ${targetId}`); }
            //         if (target.regExp === previousDefaultRegex) {
            //             doseTargetAdapter.updateOne(state.doseTargets, { id: targetId, changes: { regExp: newDefaultRegex, isModified: true } });
            //         }
            //     }
            // }

            doseOutputAdapter.updateOne(state.doseOutputs, { id: outputId, changes: { targetMethod: targetMethod } });

            setObjectAndAncestorsAsModified(state, [outputId], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Sets dose unit for dose output's targets.
         * @param action.id ID of the dose output.
         * @param action.doseUnit The value to set the unit field to.
         */
        doseTargetUnitSet(state, action: PayloadAction<{ outputId: string, doseUnit: SelectedDoseUnit }>) {
            const { outputId, doseUnit } = action.payload;

            const output = state.doseOutputs.entities[outputId];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${outputId}`); }

            doseOutputAdapter.updateOne(state.doseOutputs, { id: outputId, changes: { targetUnit: doseUnit } })

            setObjectAndAncestorsAsModified(state, [outputId], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Sets a dose target's ROI name.
         * @param action.id ID of the dose target.
         * @param action.regExp The value to set the roiName field to.
         */
        doseTargetRoiNameSet(state, action: PayloadAction<{ id: string, roiName: string }>) {
            const { id, roiName } = action.payload;

            const target = state.doseTargets.entities[id];
            if (target === undefined) { throw new Error(`Could not find dose target with id ${id}`); }

            doseTargetAdapter.updateOne(state.doseTargets, { id: id, changes: { roiName } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.Target);
        },

        /**
         * Sets a dose target's regular expression (match pattern).
         * @param action.id ID of the dose target.
         * @param action.regExp The value to set the regExp field to.
         */
        doseTargetRegExpSet(state, action: PayloadAction<{ id: string, regExp: string }>) {
            const { id, regExp } = action.payload;

            const target = state.doseTargets.entities[id];
            if (target === undefined) { throw new Error(`Could not find dose target with id ${id}`); }

            doseTargetAdapter.updateOne(state.doseTargets, { id: id, changes: { regExp } });
            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.Target);
        },

        /**
         * Sets a dose target's prescription.
         * @param action.id ID of the dose target.
         * @param action.doseValue The dose value to set the prescription field to. The unit defined in the parent output
         * object will be used.
         */
        doseTargetPrescriptionSet(state, action: PayloadAction<{ id: string, doseValue: number | null }>) {
            const { id, doseValue } = action.payload;

            const target = state.doseTargets.entities[id];
            if (target === undefined) { throw new Error(`Could not find dose target with id ${id}`); }

            if (target.outputId === undefined) { throw new Error('Target does not have parent output defined.'); }

            const output = state.doseOutputs.entities[target.outputId];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${target.outputId}`); }

            doseTargetAdapter.updateOne(state.doseTargets, { id: id, changes: { prescription: doseValue } });

            setObjectAndAncestorsAsModified(state, [id], CustomizationObjectType.Target);
        },

        /**
        * Adds a new dose target with default settings to the given dose output.
        * @param action ID of the dose output.
        */
        newDoseTargetAdded(state, action: PayloadAction<string>) {
            const outputId = action.payload;

            const output = state.doseOutputs.entities[outputId];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${outputId}`); }

            const target = defaultCreateNewDoseTarget(outputId, output.targetMethod);
            doseTargetAdapter.addOne(state.doseTargets, target);
            doseOutputAdapter.updateOne(state.doseOutputs, { id: outputId, changes: { targets: output.targets.concat(target.id) } });
            setObjectAndAncestorsAsModified(state, [outputId], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Removes dose target with given ID
         * @param action ID of the dose target.
         */
        doseTargetRemoved(state, action: PayloadAction<string>) {
            const targetId = action.payload;

            const target = state.doseTargets.entities[targetId];
            if (target === undefined) { throw new Error(`Could not find dose target with id ${targetId}`); }

            // try to get outputId -- the field is technically optional, but the UI should NOT ever be in
            // a state where the user is allowed to delete a dose target that's not correctly linked to
            // a parent output
            const outputId = target.outputId;
            if (!outputId) { throw new Error('Dose target is not linked to a valid output'); }
            const output = state.doseOutputs.entities[outputId];
            if (output === undefined) { throw new Error(`Could not find dose output with id ${outputId}`); }

            doseOutputAdapter.updateOne(state.doseOutputs, { id: outputId, changes: { targets: output.targets.filter(tId => tId !== targetId) } });
            doseTargetAdapter.removeOne(state.doseTargets, targetId);

            setObjectAndAncestorsAsModified(state, [outputId], CustomizationObjectType.CustomizationOutput);
        },

        /**
         * Reverts a dose target to its original values (if available).
         * @param action ID of the dose target.
         */
        doseTargetChangesReverted(state, action: PayloadAction<string>) {
            const targetId = action.payload;

            const originalTarget = state.originalDoseTargets.entities[targetId];
            if (originalTarget) {
                doseTargetAdapter.upsertOne(state.doseTargets, { ...originalTarget, });

                // remove any validation errors
                backendValidationErrorAdapter.removeOne(state.backendValidationErrors, targetId);
            }
        },

        /**
         * Performs an undo operation for all dose customization data, reverting all dose customization entities
         * to their original values from backend. Any new unsaved entries are lost.
         */
        allCustomizationChangesReverted(state) {
            modelAdapter.setAll(state.models, state.originalModels.entities as Record<string, Model>);
            customizationBaseAdapter.setAll(state.customizationBases, state.originalCustomizationBases.entities as Record<string, CustomizationBase>);
            doseOutputAdapter.setAll(state.doseOutputs, state.originalDoseOutputs.entities as Record<string, DoseCustomizationOutput>);
            outputMetadataAdapter.setAll(state.outputMetadata, state.originalOutputMetadata.entities as Record<string, OutputMetadataItem>);
            doseRoiAdapter.setAll(state.doseRois, state.originalDoseRois.entities as Record<string, DoseRoi>);
            doseTargetAdapter.setAll(state.doseTargets, state.originalDoseTargets.entities as Record<string, DoseTarget>);
            aeTitleRuleAdapter.setAll(state.aeTitleRules, state.originalAeTitleRules.entities as Record<string, AeTitleRule>);
            dicomRuleAdapter.setAll(state.dicomRules, state.originalDicomRules.entities as Record<string, DicomRule>);
            dicomAttributeRuleAdapter.setAll(state.dicomAttributeRules, state.originalDicomAttributeRules.entities as Record<string, DicomAttributeRule>);

            // remove any validation errors
            backendValidationErrorAdapter.removeAll(state.backendValidationErrors);
            sliceWrapper.clearSaveErrors(state);
        },

        /**
         * Marks any supplied dose customization entities as having been modified.
         * @param action A collection of customization entities to mark as modified as per their
         * entity adapter IDs.
         */
        modelCustomizationObjectsSetAsModified(state, action: PayloadAction<Partial<DoseCustomizationEntities>>) {
            const entitiesToUpdate = action.payload;

            sliceWrapper.setStateObjectsAsModified(state, entitiesToUpdate);
        },

        /**
         * Signals that entire dose model customization should be saved to backend.
         * @param action Current configuration target (AppClient).
         */
        modelCustomizationSaved(state, action: PayloadAction<{ configurationTarget: MVisionAppClient, force?: boolean }>) {
            // this is an empty action and is only used for signalling in sagas
        },

        /**
         * Marks dose customization save as being in progress.
         */
        saveModelCustomizationStarted(state) {
            state.isModelCustomizationSavingInProgress = true;
            state.concurrentSaveError = null;
        },

        /**
         * Marks dose customization save as having been finished, whether in success or failure.
         * @param action.saveWasSuccessful True if save succeeded, false otherwise.
         * @param action.errorMessage An optional error message if save was unsuccessful,
         * @param action.error An optional backend validation error object if save was unsuccessful.
         * @param action.concurrentSaveError An optional concurrent save error if the save failed due to a conflict.
         * @param action.updatedTimestamps An optional record of model name to updated timestamp, for updating lastModifiedTimestamp on successful save.
         */
        saveModelCustomizationFinished(state, action: PayloadAction<{ saveWasSuccessful: boolean, errorMessage?: string, error?: BackendValidationError, concurrentSaveError?: ConcurrentSaveError, updatedTimestamps?: Record<string, number> }>) {
            const { saveWasSuccessful, errorMessage, error, concurrentSaveError, updatedTimestamps } = action.payload;
            state.isModelCustomizationSavingInProgress = false;

            // remove existing backend validation errors -- we may add new ones more in just a moment
            // (DON'T remove FORM validation errors though -- we may have warnings there that still apply)
            backendValidationErrorAdapter.removeAll(state.backendValidationErrors);

            if (saveWasSuccessful) {
                // set everything back to unmodified & store as the new original entities
                Object.values(state.models.entities).forEach(m => { if (m !== undefined) { m.isModified = false } });
                Object.values(state.customizationBases.entities).forEach(c => { if (c !== undefined) { c.isModified = false } });
                Object.values(state.doseOutputs.entities).forEach(c => { if (c !== undefined) { c.isModified = false } });
                Object.values(state.outputMetadata.entities).forEach(md => { if (md !== undefined) { md.isModified = false } });
                Object.values(state.doseRois.entities).forEach(r => { if (r !== undefined) { r.isModified = false } });
                Object.values(state.doseTargets.entities).forEach(r => { if (r !== undefined) { r.isModified = false } });
                Object.values(state.aeTitleRules.entities).forEach(r => { if (r !== undefined) { r.isModified = false; r.isNew = false; } });
                Object.values(state.dicomRules.entities).forEach(r => { if (r !== undefined) { r.isModified = false; r.isNew = false; } });
                Object.values(state.dicomAttributeRules.entities).forEach(r => { if (r !== undefined) { r.isModified = false; r.isNew = false; } });

                modelAdapter.setAll(state.originalModels, state.models.entities as Record<string, Model>);
                customizationBaseAdapter.setAll(state.originalCustomizationBases, state.customizationBases.entities as Record<string, CustomizationBase>);
                doseOutputAdapter.setAll(state.originalDoseOutputs, state.doseOutputs.entities as Record<string, DoseCustomizationOutput>);
                outputMetadataAdapter.setAll(state.originalOutputMetadata, state.outputMetadata.entities as Record<string, OutputMetadataItem>);
                doseRoiAdapter.setAll(state.originalDoseRois, state.doseRois.entities as Record<string, DoseRoi>);
                doseTargetAdapter.setAll(state.originalDoseTargets, state.doseTargets.entities as Record<string, DoseTarget>);
                aeTitleRuleAdapter.setAll(state.originalAeTitleRules, state.aeTitleRules.entities as Record<string, AeTitleRule>);
                dicomRuleAdapter.setAll(state.originalDicomRules, state.dicomRules.entities as Record<string, DicomRule>);
                dicomAttributeRuleAdapter.setAll(state.originalDicomAttributeRules, state.dicomAttributeRules.entities as Record<string, DicomAttributeRule>);

                // update lastModifiedTimestamp on models & originalModels from backend response
                updateModelTimestampsAfterSave(state, updatedTimestamps);
            }

            state.modelCustomizationSaveError = errorMessage !== undefined ? errorMessage : null;
            state.concurrentSaveError = concurrentSaveError ?? null;

            // collect save errors
            sliceWrapper.collectSaveErrors(state, saveWasSuccessful, error);
        },

        /**
         * Duplicates given customization base and any entities under it.
         * @param action.customizationBaseId The ID of the customization base to duplicate.
         * @param action.newCustomizationName The name of the new duplicate customization base.
         */
        customizationBaseDuplicated(state, action: PayloadAction<{ customizationBaseId: string, newCustomizationName: string }>) {
            // this is an empty action and is only used for signalling in sagas
        },

        /**
        * Adds duplicated customization base items into redux store. Duplication itself is done in a saga function.
        */
        customizationDuplicationItemsAdded(state, action: PayloadAction<{
            parentModelId: string,
            customization: CustomizationBase,
            outputs: DoseCustomizationOutput[],
            metadata: OutputMetadataItem[],
            rois: DoseRoi[],
            targets: DoseTarget[],
            duplicatedIds: DuplicatedIdMap[]
        }>) {
            const { parentModelId, customization, outputs, metadata, rois, targets, duplicatedIds } = action.payload;

            const parentModel = state.models.entities[parentModelId];
            if (!parentModel) { throw new Error('No parent model found for duplication target'); }

            customizationBaseAdapter.addOne(state.customizationBases, customization);
            doseOutputAdapter.addMany(state.doseOutputs, outputs);
            outputMetadataAdapter.addMany(state.outputMetadata, metadata);
            doseRoiAdapter.addMany(state.doseRois, rois);
            doseTargetAdapter.addMany(state.doseTargets, targets);

            modelAdapter.updateOne(state.models, {
                id: parentModelId,
                changes: { customizations: parentModel.customizations.concat(customization.id) }
            });

            setObjectAndAncestorsAsModified(state, [parentModelId], CustomizationObjectType.Model);
        },

        /**
         * Removes the specified customization base entity and all entities under it in the hierarchy.
         * @param action The ID of the customization base to remove.
         */
        customizationBaseRemoved(state, action: PayloadAction<string>) {
            const customizationBaseId = action.payload;

            sliceWrapper.removeCustomizationBase(state, customizationBaseId);
        },

        /**
         * Signals that all current dose customizations should be reset back to factory default values.
         * @param action Current configuration target
         */
        allModelCustomizationsReset(state, action: PayloadAction<MVisionAppClient>) {
            // this is an empty action and is only used for signalling in sagas
        },

        /** Marks dose customization reset as having started. */
        resetAllModelCustomizationsStarted(state) {
            state.isAllModelCustomizationsResetInProgress = true;
        },

        /** Marks model customization reset as finished. 
         * @param action.resetWasSuccessful True if reset was successful, false otherwise.
         * @param action.errorMessage Optional error message if output reset failed.
         */
        resetAllModelCustomizationsFinished(state, action: PayloadAction<{ resetWasSuccessful: boolean, errorMessage?: string }>) {
            const { resetWasSuccessful, errorMessage } = action.payload;

            sliceWrapper.finishResetAllModelCustomizations(state, resetWasSuccessful, errorMessage);
        },


        /** Resets the customization import export error. */
        resetCustomizationImportExportError(state) {
            state.customizationImportExportError = null;
        },

        /**
         * Signals that a single customization base should be reset back to factory default values.
         * @param action Internal ID of the customization base to reset.
         */
        singleCustomizationReset(state, action: PayloadAction<string>) {
            // this is an empty action and is only used for signalling in sagas
        },

        /** Marks a single dose output reset as having started. */
        resetSingleOutputStarted(state) {
            state.isOutputResetInProgress = true;
        },

        /** Marks a single dose output reset as finished.
         * @param action.resetWasSuccessful True if reset was succesful, false otherwise.
         * @param action.errorMessage Optional error message if output reset failed.
         */
        resetSingleOutputFinished(state, action: PayloadAction<{ resetWasSuccessful: boolean, errorMessage?: string }>) {
            state.isOutputResetInProgress = false;
            state.modelCustomizationDataError = action.payload.errorMessage || null;
        },

        /**
         * Replaces a specified dose customization output with supplied values, including ROIs, targets and metadata. ID of the original 
         * dose output is retained.
         * @param action.customizationOutputId The ID of the dose output to be replaced.
         * @param action.newOutputEntities The values that will be used to replace the dose output.
         */
        customizationOutputReplaced(state, action: PayloadAction<{ customizationOutputId: string, newOutputEntities: DoseCustomizationOutputEntities }>) {
            const { customizationOutputId, newOutputEntities } = action.payload;
            const originalOutput = state.doseOutputs.entities[customizationOutputId];

            if (!originalOutput) { throw new Error(`No original dose output (${customizationOutputId}) to replace!`); }
            if (newOutputEntities.outputs.length < 1) { throw new Error('No replacement dose output given'); }

            // currently only support replacing first output from given input even if we'd get several
            const newOutput = newOutputEntities.outputs[0];
            const newMetadata = newOutputEntities.metadata.filter(m => newOutput.metadata.includes(m.id));
            const newRois = newOutputEntities.rois.filter(r => newOutput.rois.includes(r.id));
            const newTargets = newOutputEntities.targets.filter(t => newOutput.targets.includes(t.id));

            // modify key parts of the replacement output items to match the original. also mark everything as modified
            newOutput.id = originalOutput.id;
            newOutput.modelCustomizationBaseId = originalOutput.modelCustomizationBaseId;
            newOutput.filename = originalOutput.filename;
            newOutput.isModified = true;

            newMetadata.forEach(m => {
                if (m.attribute === METADATA_FILENAME_ATTRIBUTE) { m.value = originalOutput.filename; }
                m.modelCustomizationOutputId = originalOutput.id;
                m.isModified = true;
            });

            newRois.forEach(r => {
                r.outputId = originalOutput.id;
                r.isModified = true;
            });

            newTargets.forEach(t => {
                t.outputId = originalOutput.id;
                t.isModified = true;
            });

            // remove old metadata, roi, and target items
            outputMetadataAdapter.removeMany(state.outputMetadata, originalOutput.metadata);
            doseRoiAdapter.removeMany(state.doseRois, originalOutput.rois);
            doseTargetAdapter.removeMany(state.doseTargets, originalOutput.targets);

            // add new items
            outputMetadataAdapter.addMany(state.outputMetadata, newMetadata);
            doseRoiAdapter.addMany(state.doseRois, newRois);
            doseTargetAdapter.addMany(state.doseTargets, newTargets);

            // swap in the new customization output
            doseOutputAdapter.setOne(state.doseOutputs, newOutput);

            // mark ancestors as modified (start with customization base as the output was already marked earlier)
            setObjectAndAncestorsAsModified(state, [newOutput.modelCustomizationBaseId], CustomizationObjectType.CustomizationBase);
        },

        /**
         * Renames given customization base.
         * @param action.customizationBaseId ID of the customization base to rename.
         * @param action.name The new name of the customization base.
         */
        customizationRenamed(state, action: PayloadAction<{ customizationBaseId: string, name: string }>) {
            const { customizationBaseId, name } = action.payload;
            customizationBaseAdapter.updateOne(state.customizationBases, { id: customizationBaseId, changes: { isModified: true, customizationName: name } });
            setObjectAndAncestorsAsModified(state, [customizationBaseId], CustomizationObjectType.CustomizationBase);
        },

        /**
         * Export entire current dose customization into a JSON file.
         * @param action Current configuration target.
         */
        modelCustomizationExported(state, action: PayloadAction<MVisionAppClient | undefined>) {
            // this is an empty action and is only used for signalling in sagas
        },

        /**
         * Imports parsed JSON file contents into current dose configuration target.
         * @param action Dose customization to import.
         */
        modelCustomizationImported(state, action: PayloadAction<DoseCustomizationEntities | null>) {
            // this is an empty action and is only used for signalling in sagas
        },

        /**
         * Marks importing of a JSON file into dose customization as failed.
         * @param action Error message, or null if error message should be reset.
         */
        modelCustomizationImportFailed(state, action: PayloadAction<string | null>) {
            const errorMessage = action.payload;
            state.customizationImportExportError = errorMessage;
        },

        /**
         * Marks exporting of a JSON file from dose customization as failed.
         * @param action Error message, or null if error message should be reset.
         */
        modelCustomizationExportFailed(state, action: PayloadAction<string | null>) {
            const errorMessage = action.payload;
            state.customizationImportExportError = errorMessage;
        },

        /**
         * Export dose customization as a PDF file via backend API.
         * @param action Current configuration target.
         */
        modelCustomizationPdfExportRequested(_state, _action: PayloadAction<MVisionAppClient | undefined>) {
            // Signal-only reducer — state is set in the saga via pdfExportStarted
        },

        /** Marks PDF export as started. */
        pdfExportStarted(state) {
            state.pdfExportInProgress = true;
            state.customizationImportExportError = null;
        },

        /** Marks PDF export as finished. */
        pdfExportFinished(state) {
            state.pdfExportInProgress = false;
        },

        /**
         * Toggles a tag on or off from active tag filters.
         * @param action label of the tag to toggle.
         */
        activeTagFilterToggled(state, action: PayloadAction<string>) {
            const tag = action.payload;
            sliceWrapper.toggleActiveTagFilter(state, tag);
        },

        /**
         * Resets (unselects) all current active tag filters.
         */
        activeTagFiltersReset(state) {
            sliceWrapper.resetActiveTagFilters(state);
        },

        /** Toggles a modality on or off from active modality filters for the given direction (input/output) and modality. */
        activeModalityFilterToggled(state, action: PayloadAction<{ direction: ModalityDirection, modality: Modality }>) {
            const { direction, modality } = action.payload;
            sliceWrapper.toggleActiveModalityFilter(state, direction, modality);
        },

        /** Resets (unselects) all current active modality filters for the given direction (input/output). */
        activeModalityFiltersReset(state, action: PayloadAction<ModalityDirection>) {
            const direction = action.payload;
            sliceWrapper.resetActiveModalityFilters(state, direction);
        },

        /** Sets the active free-text filter value. */
        activeFreeTextFilterSet(state, action: PayloadAction<string>) {
            const value = action.payload;
            sliceWrapper.setActiveFreeTextFilter(state, value);
        },

        /** Resets the active free-text filter to an empty string. */
        activeFreeTextFilterReset(state) {
            sliceWrapper.resetActiveFreeTextFilter(state);
        },

        /** Sets whether deprecated models are shown. */
        deprecatedModelsVisibilitySet(state, action: PayloadAction<boolean>) {
            sliceWrapper.setAreDeprecatedModelsShown(state, action.payload);
        },

        /** Sets whether non-licensed models are shown. */
        nonLicensedModelsVisibilitySet(state, action: PayloadAction<boolean>) {
            sliceWrapper.setAreNonLicensedModelsShown(state, action.payload);
        },

        /** Sets whether user-hidden models are shown. */
        userHiddenModelsVisibilitySet(state, action: PayloadAction<boolean>) {
            sliceWrapper.setAreUserHiddenModelsShown(state, action.payload);
        },

        /**
         * Toggles a tag on or off from active tag filters.
         * @param action label of the tag to toggle.
         */
        activeVersionTagFilterToggled(state, action: PayloadAction<string>) {
            const tag = action.payload;
            sliceWrapper.toggleActiveVersionTagFilter(state, tag);
        },

        /**
         * Resets (unselects) all current active version tag filters.
         */
        activeVersionTagFiltersReset(state) {
            sliceWrapper.resetActiveVersionTagFilters(state);
        },


        /** Unit test helper reducer for setting an object and all its ancestors as modified.
         *
         * Do not use this reducer outside of unit tests!
         */
        _unitTest_customizationObjectPathSetAsModified(state, action: PayloadAction<{ objectIds: string[], objectType: CustomizationObjectType }>) {
            const { objectIds, objectType } = action.payload;
            setObjectAndAncestorsAsModified(state, objectIds, objectType);
        },

        /** Unit test helper reducer for adding a backend validation error.
         *
         * Do not use this reducer outside of unit tests!
         */
        _unitTest_backendValidationErrorAdded(state, action: PayloadAction<BackendValidationErrorViewModel>) {
            const backendValidationError = action.payload;
            backendValidationErrorAdapter.addOne(state.backendValidationErrors, backendValidationError);
        },
    },
    selectors: {

        /** Returns all tags that are currently used for filtering */
        selectActiveTags: (state): string[] => state.activeTagFilters,

        /** Returns all active input modality filters */
        selectActiveInputModalities: (state): Modality[] => state.activeInputModalityFilters,

        /** Returns all active output modality filters */
        selectActiveOutputModalities: (state): Modality[] => state.activeOutputModalityFilters,


        /** Returns all active version tags */
        selectActiveVersionTags: (state): string[] => state.activeVersionTags,

        /** Returns the active free-text filter value */
        selectActiveFreeTextFilter: (state): string => state.activeFreeTextFilter,

        /** Returns whether deprecated models are shown */
        selectAreDeprecatedModelsShown: (state): boolean => state.areDeprecatedModelsShown,

        /** Returns whether non-licensed models are shown */
        selectAreNonLicensedModelsShown: (state): boolean => state.areNonLicensedModelsShown,

        /** Returns whether user-hidden models are shown */
        selectAreUserHiddenModelsShown: (state): boolean => state.areUserHiddenModelsShown,

        /** Returns the top-level model name for a dose output */
        selectModelNameForOutput: (state, outputId: string) => {
            const output = localSelectors.selectOutputById(state, outputId);
            if (output) {
                const customization = localSelectors.selectCustomizationBaseById(state, output.modelCustomizationBaseId);
                if (customization) {
                    const model = localSelectors.selectModelById(state, customization.modelId);
                    if (model) {
                        return model.modelName;
                    }
                }
            }
        },

        /** Returns true if any model customization object has been modified. */
        selectIsAnyCustomizationModelModified: createSelector(
            (state: DoseSliceState): Model[] => localSelectors.selectModels(state),
            (models) => models.some(m => m.isModified)),

        /** Returns an array of all customization names and their internal parent model IDs. */
        selectAllCustomizationNamesAndParentModelIds: createSelector(
            (state: DoseSliceState): CustomizationBase[] => localSelectors.selectCustomizationBases(state),
            (customizations) => customizations.map(c => ({ customizationName: c.customizationName, modelId: c.modelId }))),

        /** Returns true if there are ANY unsaved DOSE+ changes in the app. */
        selectAnyUnsavedDoseChangesInApp: (state) => localSelectors.selectIsAnyCustomizationModelModified(state),

        // ENTITY ADAPTER SELECTORS:
        // for all supported createEntityAdapter selectors see https://redux-toolkit.js.org/api/createEntityAdapter#selector-functions

        selectModels: (state): Model[] => modelAdapterSelectors.selectAll(state),
        selectModelEntities: (state) => modelAdapterSelectors.selectEntities(state),
        selectModelById: (state, id: string): Model | undefined => modelAdapterSelectors.selectById(state, id),

        selectCustomizationBases: (state) => customizationBaseAdapterSelectors.selectAll(state),
        selectCustomizationBaseIds: (state) => customizationBaseAdapterSelectors.selectIds(state),
        selectCustomizationBaseById: (state, id: string): CustomizationBase | undefined => customizationBaseAdapterSelectors.selectById(state, id),
        selectCustomizationBaseEntities: (state) => customizationBaseAdapterSelectors.selectEntities(state),
        selectCustomizationBasesInAlphabeticalOrder: createSelector(
            [(state: DoseSliceState): CustomizationBase[] => localSelectors.selectCustomizationBases(state)],
            bases => naturalSort(bases, 'customizationName')),

        selectOutputs: (state) => doseOutputAdapterSelectors.selectAll(state),
        selectOutputIds: (state) => doseOutputAdapterSelectors.selectIds(state),
        selectOutputEntities: (state) => doseOutputAdapterSelectors.selectEntities(state),
        selectOutputById: (state, id: string): DoseCustomizationOutput | undefined => doseOutputAdapterSelectors.selectById(state, id),

        selectRois: (state) => doseRoiAdapterSelectors.selectAll(state),
        selectRoiById: (state, id: string): DoseRoi | undefined => doseRoiAdapterSelectors.selectById(state, id),
        selectRoiEntities: (state) => doseRoiAdapterSelectors.selectEntities(state),
        selectOriginalRoiById: (state, id: string): DoseRoi | undefined => originalDoseRoiAdapterSelectors.selectById(state, id),

        selectTargets: (state) => doseTargetAdapterSelectors.selectAll(state),
        selectTargetById: (state, id: string): DoseTarget | undefined => doseTargetAdapterSelectors.selectById(state, id),
        selectTargetEntities: (state) => doseTargetAdapterSelectors.selectEntities(state),
        selectOriginalTargetById: (state, id: string): DoseTarget | undefined => originalDoseTargetAdapterSelectors.selectById(state, id),
        selectTargetIds: (state) => doseTargetAdapterSelectors.selectIds(state),

        selectOutputMetadata: (state) => outputMetadataAdapterSelectors.selectAll(state),
        selectOutputMetadataEntities: (state) => outputMetadataAdapterSelectors.selectEntities(state),
        selectOriginalOutputMetadataEntities: (state) => originalOutputMetadataAdapterSelectors.selectEntities(state),
        selectOutputMetadataById: (state, id: string): OutputMetadataItem | undefined => outputMetadataAdapterSelectors.selectById(state, id),

        selectAeTitleRules: (state) => aeTitleRuleAdapterSelectors.selectAll(state),
        selectAeTitleRuleEntities: (state) => aeTitleRuleAdapterSelectors.selectEntities(state),
        selectAeTitleRuleById: (state, id: string): AeTitleRule | undefined => aeTitleRuleAdapterSelectors.selectById(state, id),

        selectDicomRules: (state) => dicomRuleAdapterSelectors.selectAll(state),
        selectDicomRuleEntities: (state) => dicomRuleAdapterSelectors.selectEntities(state),
        selectDicomRuleById: (state, id: string): DicomRule | undefined => dicomRuleAdapterSelectors.selectById(state, id),

        selectDicomAttributeRules: (state) => dicomAttributeRuleAdapterSelectors.selectAll(state),
        selectDicomAttributeRuleById: (state, id: string): DicomAttributeRule | undefined => dicomAttributeRuleAdapterSelectors.selectById(state, id),

        selectCustomizationValidationError: (state, id: string): BackendValidationErrorViewModel | undefined => customizationValidationErrorAdapterSelectors.selectById(state, id),
        selectCustomizationValidationErrorEntities: (state) => customizationValidationErrorAdapterSelectors.selectEntities(state),

        /** Form validation errors for the Dose+ slice — derived on demand from current entity state.
         *  Composes trigger-rule errors (DICOM rule / DICOM attribute / AE title uniqueness) across the
         *  whole slice, plus per-output Dose+ ring-distance errors when ROI creation is enabled. */
        selectFormValidationErrors: createSelector(
            [
                (state: DoseSliceState): DicomRule[] => localSelectors.selectDicomRules(state),
                (state: DoseSliceState): DicomAttributeRule[] => localSelectors.selectDicomAttributeRules(state),
                (state: DoseSliceState): AeTitleRule[] => localSelectors.selectAeTitleRules(state),
                (state: DoseSliceState): DoseCustomizationOutput[] => localSelectors.selectOutputs(state),
            ],
            (dicomRules, dicomAttributeRules, aeTitleRules, outputs) => {
                const errors: FormValidationError[] = [];
                errors.push(...composeTriggerValidationErrors(dicomRules, dicomAttributeRules, aeTitleRules));
                for (const output of outputs) {
                    errors.push(...composeDoseOutputValidationErrors(
                        output.id,
                        output.optimization.isRoiCreationEnabled,
                        output.optimization.targetUnionRings.distances,
                    ));
                }
                return errors;
            }
        ),

        /** Returns the set of customization-base ids that have at least one Error-severity form
         *  validation error among their descendants. Each error's `(itemType, itemId)` is walked
         *  to its owning customization base via `resolveCustomizationBaseIdForError`. Warning-
         *  severity errors are excluded; only blocking errors mark a base. */
        selectCustomizationBaseIdsWithBlockingFormValidationErrors: createSelector(
            [
                (state: DoseSliceState): FormValidationError[] => localSelectors.selectFormValidationErrors(state),
                (state: DoseSliceState): Record<string, AeTitleRule | undefined> => localSelectors.selectAeTitleRuleEntities(state),
                (state: DoseSliceState): Record<string, DicomRule | undefined> => localSelectors.selectDicomRuleEntities(state),
                (state: DoseSliceState): Record<string, DicomAttributeRule | undefined> => dicomAttributeRuleAdapterSelectors.selectEntities(state),
                (state: DoseSliceState): Record<string, DoseRoi | undefined> => localSelectors.selectRoiEntities(state),
                (state: DoseSliceState): Record<string, DoseTarget | undefined> => localSelectors.selectTargetEntities(state),
                (state: DoseSliceState): Record<string, DoseCustomizationOutput | undefined> => localSelectors.selectOutputEntities(state),
                (state: DoseSliceState): Record<string, OutputMetadataItem | undefined> => localSelectors.selectOutputMetadataEntities(state),
            ],
            (errors, aeTitleRules, dicomRules, dicomAttributeRules, rois, targets, outputs, metadata) => {
                const baseIds = new Set<string>();
                for (const err of errors) {
                    if (err.severity !== FormValidationErrorSeverity.Error) { continue; }
                    const baseId = resolveCustomizationBaseIdForError(err.itemType, err.itemId, {
                        aeTitleRules, dicomRules, dicomAttributeRules, rois, targets, outputs, metadata,
                    });
                    if (baseId) { baseIds.add(baseId); }
                }
                return baseIds;
            }
        ),

        /** Returns the set of customization-base ids that have at least one backend validation
         *  error keyed to a descendant entity. Backend errors are populated from server 4xx
         *  responses on save and live in the `backendValidationErrors` adapter; each error's
         *  `(type, id)` is walked to its owning customization base via
         *  `resolveCustomizationBaseIdForError`. */
        selectCustomizationBaseIdsWithBackendValidationErrors: createSelector(
            [
                (state: DoseSliceState): Record<string, BackendValidationErrorViewModel | undefined> => localSelectors.selectCustomizationValidationErrorEntities(state),
                (state: DoseSliceState): Record<string, AeTitleRule | undefined> => localSelectors.selectAeTitleRuleEntities(state),
                (state: DoseSliceState): Record<string, DicomRule | undefined> => localSelectors.selectDicomRuleEntities(state),
                (state: DoseSliceState): Record<string, DicomAttributeRule | undefined> => dicomAttributeRuleAdapterSelectors.selectEntities(state),
                (state: DoseSliceState): Record<string, DoseRoi | undefined> => localSelectors.selectRoiEntities(state),
                (state: DoseSliceState): Record<string, DoseTarget | undefined> => localSelectors.selectTargetEntities(state),
                (state: DoseSliceState): Record<string, DoseCustomizationOutput | undefined> => localSelectors.selectOutputEntities(state),
                (state: DoseSliceState): Record<string, OutputMetadataItem | undefined> => localSelectors.selectOutputMetadataEntities(state),
            ],
            (errorEntities, aeTitleRules, dicomRules, dicomAttributeRules, rois, targets, outputs, metadata) => {
                const baseIds = new Set<string>();
                for (const err of Object.values(errorEntities)) {
                    if (!err) { continue; }
                    const baseId = resolveCustomizationBaseIdForError(err.type, err.id, {
                        aeTitleRules, dicomRules, dicomAttributeRules, rois, targets, outputs, metadata,
                    });
                    if (baseId) { baseIds.add(baseId); }
                }
                return baseIds;
            }
        ),
    }
});

/** Walks an entity reference (form-validation OR backend-validation error) to its owning Dose+
 *  customization base id, or returns undefined if the chain can't be resolved. Used by both the
 *  form-error and backend-error tab markers. */
const resolveCustomizationBaseIdForError = (
    itemType: CustomizationObjectType,
    itemId: string,
    refs: {
        aeTitleRules: Record<string, AeTitleRule | undefined>,
        dicomRules: Record<string, DicomRule | undefined>,
        dicomAttributeRules: Record<string, DicomAttributeRule | undefined>,
        rois: Record<string, DoseRoi | undefined>,
        targets: Record<string, DoseTarget | undefined>,
        outputs: Record<string, DoseCustomizationOutput | undefined>,
        metadata: Record<string, OutputMetadataItem | undefined>,
    },
): string | undefined => {
    switch (itemType) {
        case CustomizationObjectType.CustomizationBase:
            return itemId;
        case CustomizationObjectType.CustomizationOutput:
            return refs.outputs[itemId]?.modelCustomizationBaseId;
        case CustomizationObjectType.AeTitleRule:
            return refs.aeTitleRules[itemId]?.modelCustomizationBaseId;
        case CustomizationObjectType.DicomRule:
            return refs.dicomRules[itemId]?.modelCustomizationBaseId;
        case CustomizationObjectType.DicomAttributeRule: {
            const parentId = refs.dicomAttributeRules[itemId]?.parentDicomRuleId;
            return parentId ? refs.dicomRules[parentId]?.modelCustomizationBaseId : undefined;
        }
        case CustomizationObjectType.Roi: {
            const outputId = refs.rois[itemId]?.outputId;
            return outputId ? refs.outputs[outputId]?.modelCustomizationBaseId : undefined;
        }
        case CustomizationObjectType.Target: {
            const outputId = refs.targets[itemId]?.outputId;
            return outputId ? refs.outputs[outputId]?.modelCustomizationBaseId : undefined;
        }
        case CustomizationObjectType.Metadata: {
            const outputId = refs.metadata[itemId]?.modelCustomizationOutputId;
            return outputId ? refs.outputs[outputId]?.modelCustomizationBaseId : undefined;
        }
        default:
            return undefined;
    }
};




export const {
    modelCustomizationsSet,
    modelVisibilityUpdated,
    modelCustomizationDescriptionUpdated,
    metadataItemUpdated,
    metadataItemRemoved,
    scrollToViewFromModelCustomizationMetadataRemoved,
    aeTitleRuleAdded,
    aeTitleRuleActionUpdated,
    aeTitleRuleRemoved,
    dicomRuleAdded,
    dicomRuleRemoved,
    dicomAttributeRuleAdded,
    dicomAttributeRuleRemoved,
    dicomAttributeRuleUpdated,
    doseScalingMethodSet,
    doseScalingVolumeSet,
    doseScalingDoseSet,
    pixelSpacingSet,
    doseCroppingIsEnabledSet,
    doseCroppingValueSet,
    outputIsRTPlanIncludedSet,
    outputIsBeamIncludedSet,
    outputMachineTypeSet,
    outputMachineNameSet,
    optimizationCreateRoisSet,
    optimizationPrefixSet,
    optimizationTargetUnionRingsEnabledSet,
    optimizationTargetUnionRingsWidthSet,
    optimizationTargetUnionRingsDistanceSet,
    optimizationSkinEnabledSet,
    optimizationSkinWidthSet,
    optimizationTargetSubtractionEnabledSet,
    optimizationTargetSubtractionMarginSet,
    optimizationPlanningRiskVolumesEnabledSet,
    optimizationPlanningRiskVolumesMarginSet,
    doseRoiNameSet,
    doseRoiRegExpSet,
    doseRoiChangesReverted,
    doseTargetMethodSet,
    doseTargetUnitSet,
    doseTargetRoiNameSet,
    doseTargetRegExpSet,
    doseTargetPrescriptionSet,
    newDoseTargetAdded,
    doseTargetRemoved,
    doseTargetChangesReverted,
    allCustomizationChangesReverted,
    modelCustomizationObjectsSetAsModified,
    modelCustomizationSaved,
    saveModelCustomizationStarted,
    saveModelCustomizationFinished,
    customizationBaseDuplicated,
    customizationDuplicationItemsAdded,
    customizationBaseRemoved,
    allModelCustomizationsReset,
    resetAllModelCustomizationsStarted,
    resetAllModelCustomizationsFinished,
    singleCustomizationReset,
    resetSingleOutputStarted,
    resetSingleOutputFinished,
    resetCustomizationImportExportError,
    customizationOutputReplaced,
    customizationRenamed,
    modelCustomizationExported,
    modelCustomizationExportFailed,
    modelCustomizationPdfExportRequested,
    pdfExportStarted,
    pdfExportFinished,
    modelCustomizationImported,
    modelCustomizationImportFailed,
    activeTagFilterToggled,
    activeTagFiltersReset,
    activeVersionTagFilterToggled,
    activeVersionTagFiltersReset,
    activeModalityFilterToggled,
    activeModalityFiltersReset,
    activeFreeTextFilterSet,
    activeFreeTextFilterReset,
    deprecatedModelsVisibilitySet,
    nonLicensedModelsVisibilitySet,
    userHiddenModelsVisibilitySet,

    _unitTest_customizationObjectPathSetAsModified,
    _unitTest_backendValidationErrorAdded,

} = doseSlice.actions;

const localSelectors = doseSlice.getSelectors();
export const localDoseSelectors = localSelectors;

export const { getInitialState, selectors: doseSelectors } = doseSlice;

export default doseSlice.reducer;
